import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { useTranslation } from 'react-i18next';

import AddLanguagePopover from './AddLanguagePopover';
import ImageOverrideRow from './ImageOverrideRow';
import VideoOverrideCard from './VideoOverrideCard';
import { LOCALIZATION_LANGUAGES } from './constants';
import { useLocalizationStyles } from './styles';
import { LocalizationAssetField } from './useSpotLocalizationUploader';
import {
  ILanguageOption,
  ISpotLocalization,
  ISpotLocalizationsMap,
  SpotMediaType,
} from './types';

interface LocalizationSectionProps {
  mediaType: SpotMediaType;
  persistedOverrides: ISpotLocalizationsMap;
  pendingCodes: string[];
  uploadingCodes: Set<string>;
  hasDefaultMedia: boolean;
  activeLanguageCode: string | null;
  onAddLanguage: (code: string) => void;
  onRemoveLanguage: (code: string) => Promise<void> | void;
  onUploadAsset: (
    code: string,
    field: LocalizationAssetField,
    file: File,
  ) => Promise<void>;
  onClearPreview: (code: string) => Promise<void> | void;
  onShowPreview: (src: string, mode: 'image' | 'video') => void;
}

const languageCodeToName = (code: string): string => {
  const match = LOCALIZATION_LANGUAGES.find((lang) => lang.code === code);
  return match ? match.name : code;
};

const orderedCodesForDisplay = (codes: string[]): string[] => {
  const indexOf = new Map<string, number>(
    LOCALIZATION_LANGUAGES.map((lang, index) => [lang.code, index]),
  );
  return [...codes].sort((a, b) => {
    const ai = indexOf.get(a) ?? Number.MAX_SAFE_INTEGER;
    const bi = indexOf.get(b) ?? Number.MAX_SAFE_INTEGER;
    return ai - bi;
  });
};

const LocalizationSection = ({
  mediaType,
  persistedOverrides,
  pendingCodes,
  uploadingCodes,
  hasDefaultMedia,
  activeLanguageCode,
  onAddLanguage,
  onRemoveLanguage,
  onUploadAsset,
  onClearPreview,
  onShowPreview,
}: LocalizationSectionProps) => {
  const { t: translate } = useTranslation();
  const styles = useLocalizationStyles();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingFileTargetRef = useRef<{
    code: string;
    field: LocalizationAssetField;
  } | null>(null);

  const allCodes = useMemo(() => {
    const set = new Set<string>([
      ...Object.keys(persistedOverrides),
      ...pendingCodes,
    ]);
    return orderedCodesForDisplay(Array.from(set));
  }, [persistedOverrides, pendingCodes]);

  const availableLanguages: ILanguageOption[] = useMemo(() => {
    const taken = new Set(allCodes);
    return LOCALIZATION_LANGUAGES.filter((lang) => !taken.has(lang.code));
  }, [allCodes]);

  const remainingCount = availableLanguages.length;
  const overrideCount = allCodes.length;

  const openFilePicker = useCallback(
    (code: string, field: LocalizationAssetField, accept: string) => {
      pendingFileTargetRef.current = { code, field };
      const input = fileInputRef.current;
      if (!input) {
        return;
      }
      input.accept = accept;
      input.value = '';
      input.click();
    },
    [],
  );

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const target = pendingFileTargetRef.current;
      pendingFileTargetRef.current = null;
      const file = event.target.files?.[0];
      if (!target || !file) {
        return;
      }
      await onUploadAsset(target.code, target.field, file);
    },
    [onUploadAsset],
  );

  const handleUploadImage = useCallback(
    (code: string) => {
      openFilePicker(code, 'media', 'image/png,image/jpeg');
    },
    [openFilePicker],
  );

  const handleUploadVideo = useCallback(
    (code: string) => {
      openFilePicker(code, 'media', 'video/mp4,video/webm,video/quicktime');
    },
    [openFilePicker],
  );

  const handleUploadPreview = useCallback(
    (code: string) => {
      openFilePicker(code, 'preview', 'image/png,image/jpeg');
    },
    [openFilePicker],
  );

  const handlePreview = useCallback(
    (code: string) => {
      const override = persistedOverrides[code];
      if (!override) {
        return;
      }
      const src = override.url || override.location;
      if (!src) {
        return;
      }
      onShowPreview(src, mediaType === 'video' ? 'video' : 'image');
    },
    [persistedOverrides, mediaType, onShowPreview],
  );

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setPopoverAnchor(event.currentTarget);
  };

  const handleClosePopover = () => {
    setPopoverAnchor(null);
  };

  const handlePickLanguage = (code: string) => {
    onAddLanguage(code);
    setPopoverAnchor(null);
  };

  const normalizedActiveLang = (activeLanguageCode ?? '').toLowerCase();

  const renderRow = (code: string) => {
    const override: ISpotLocalization = persistedOverrides[code] ?? {};
    const languageName = languageCodeToName(code);
    const isUploading = uploadingCodes.has(code);
    const isActive = code.toLowerCase() === normalizedActiveLang;

    if (mediaType === 'video') {
      return (
        <VideoOverrideCard
          key={code}
          code={code}
          languageName={languageName}
          override={override}
          isConverting={isUploading}
          isActive={isActive}
          onUploadVideo={handleUploadVideo}
          onReplaceVideo={handleUploadVideo}
          onUploadPreview={handleUploadPreview}
          onReplacePreview={handleUploadPreview}
          onUseDefaultPreview={onClearPreview}
          onPreview={handlePreview}
          onRemove={onRemoveLanguage}
        />
      );
    }

    return (
      <ImageOverrideRow
        key={code}
        code={code}
        languageName={languageName}
        override={override}
        isActive={isActive}
        onUpload={handleUploadImage}
        onReplace={handleUploadImage}
        onPreview={handlePreview}
        onRemove={onRemoveLanguage}
      />
    );
  };

  return (
    <Box sx={styles.card}>
      <Box
        sx={styles.header}
        onClick={() => setIsCollapsed((prev) => !prev)}
        role="button"
        aria-expanded={!isCollapsed}
      >
        <PublicOutlinedIcon sx={styles.headerIcon} />
        <Typography sx={styles.headerTitle}>
          {translate('editorMode.localization', 'Localization')}
        </Typography>
        <Typography
          sx={[
            styles.badge,
            ...(overrideCount === 0 ? [styles.badgeZero] : []),
          ]}
        >
          {overrideCount}
        </Typography>
        {isCollapsed && overrideCount > 0 && (
          <Typography sx={styles.codes}>{allCodes.join(' · ')}</Typography>
        )}
        <KeyboardArrowDownIcon
          sx={[styles.chev, ...(isCollapsed ? [styles.chevCollapsed] : [])]}
        />
      </Box>

      {!isCollapsed && (
        <Box sx={styles.body}>
          {!hasDefaultMedia ? (
            <Typography sx={styles.empty}>
              <Box component="span" sx={styles.emptyStrong}>
                {mediaType === 'video'
                  ? translate(
                      'editorMode.localizationBlockedVideo',
                      'Upload a default video first to enable per-language overrides.',
                    )
                  : translate(
                      'editorMode.localizationBlockedImage',
                      'Upload a default image first to enable per-language overrides.',
                    )}
              </Box>
            </Typography>
          ) : overrideCount === 0 ? (
            <Typography sx={styles.empty}>
              <Box component="span" sx={styles.emptyStrong}>
                {translate(
                  'editorMode.localizationEmptyTitle',
                  'No overrides yet.',
                )}
              </Box>{' '}
              {mediaType === 'video'
                ? translate(
                    'editorMode.localizationEmptyVideo',
                    'The default video is shown to every language. Add a language to override it for that locale only.',
                  )
                : translate(
                    'editorMode.localizationEmptyImage',
                    'The default image is shown to every language. Add a language to override it for that locale only.',
                  )}
            </Typography>
          ) : (
            allCodes.map(renderRow)
          )}

          <Button
            variant="outlined"
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            sx={styles.addButton}
            onClick={handleOpenPopover}
            disabled={remainingCount === 0 || !hasDefaultMedia}
          >
            {translate('editorMode.addLanguage', 'Add language')}
            <Box component="span" sx={styles.addButtonRemaining}>
              ·{' '}
              {remainingCount === 0
                ? translate('editorMode.allLanguagesAdded', 'all added')
                : translate(
                    'editorMode.languagesRemaining',
                    '{{count}} remaining',
                    { count: remainingCount },
                  )}
            </Box>
          </Button>
        </Box>
      )}

      <AddLanguagePopover
        anchorEl={popoverAnchor}
        open={Boolean(popoverAnchor)}
        onClose={handleClosePopover}
        availableLanguages={availableLanguages}
        onPick={handlePickLanguage}
      />

      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </Box>
  );
};

export default LocalizationSection;
